import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, Trash2, Package, Leaf, Gift, CheckCircle2, XCircle, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AntiWasteBoxPage() {
  const [boxes, setBoxes] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingReservation, setUpdatingReservation] = useState(null);
  const [activeTab, setActiveTab] = useState('boxes'); // 'boxes' | 'reservations'
  const [form, setForm] = useState({
    name: 'Colis anti-gaspi', description: '', original_value: '', price: '',
    quantity_available: '1', pickup_start: '17:00', pickup_end: '19:00', contents: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    try {
      const [boxesRes, licenseRes, reservRes] = await Promise.all([
        fetch(`${API}/business/anti-waste-boxes`, { headers }),
        fetch(`${API}/business/license-status`, { headers }),
        fetch(`${API}/business/anti-waste-reservations`, { headers })
      ]);
      if (boxesRes.ok) setBoxes(await boxesRes.json());
      if (licenseRes.ok) setLicenseStatus(await licenseRes.json());
      if (reservRes.ok) setReservations(await reservRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const createBox = async () => {
    if (!form.original_value || !form.price) return toast.error("Valeur et prix requis");
    setSaving(true);
    try {
      const body = {
        name: form.name,
        description: form.description,
        original_value: parseFloat(form.original_value),
        price: parseFloat(form.price),
        quantity_available: parseInt(form.quantity_available) || 1,
        pickup_start: form.pickup_start,
        pickup_end: form.pickup_end,
        contents: form.contents ? form.contents.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      const res = await fetch(`${API}/business/anti-waste-box`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail);
      const data = await res.json();
      toast.success("Colis anti-gaspi créé !");
      if (data.license_free_earned) {
        toast.success("Votre licence est offerte ce mois-ci !", { duration: 5000 });
      }
      setShowForm(false);
      setForm({ name: 'Colis anti-gaspi', description: '', original_value: '', price: '', quantity_available: '1', pickup_start: '17:00', pickup_end: '19:00', contents: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const updateReservationStatus = async (reservationId, status) => {
    setUpdatingReservation(reservationId);
    try {
      const res = await fetch(`${API}/anti-waste-reservations/${reservationId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const data = await res.json();
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.message || "Erreur"); }
    setUpdatingReservation(null);
  };

  const pendingReservations = reservations.filter(r => r.status === 'reserved');

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/business/dashboard"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <div>
              <h1 className="text-xl font-bold" data-testid="anti-waste-page-title">Colis Anti-Gaspi</h1>
              <p className="text-sm text-muted-foreground">{boxes.length} colis · {pendingReservations.length} réservation(s) en attente</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(true)} data-testid="add-anti-waste-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau colis
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* License Status Card */}
        {licenseStatus && (
          <Card className="shadow-lg bg-gradient-to-br from-green-50 to-emerald-50" data-testid="license-status-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold">Licence {licenseStatus.plan_name} — {licenseStatus.license_price}€/mois</h3>
              </div>
              {licenseStatus.license_free_this_month ? (
                <div className="p-3 bg-emerald-100 rounded-xl text-center">
                  <p className="text-emerald-800 font-bold text-lg">Licence OFFERTE ce mois !</p>
                  <p className="text-emerald-600 text-sm">Merci pour vos {licenseStatus.anti_waste_boxes_this_month} colis anti-gaspi</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Valeur anti-gaspi ce mois : <strong>{licenseStatus.anti_waste_value_this_month}€</strong></span>
                    <span>Objectif : {licenseStatus.threshold_for_free}€</span>
                  </div>
                  <Progress value={licenseStatus.progress_percent} className="h-3" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Encore {licenseStatus.remaining_to_free}€ de valeur anti-gaspi pour obtenir votre licence offerte
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2" data-testid="aw-tabs">
          <Button
            variant={activeTab === 'boxes' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('boxes')}
            className="rounded-full"
            data-testid="tab-boxes"
          >
            <Package className="w-4 h-4 mr-1" /> Mes colis ({boxes.length})
          </Button>
          <Button
            variant={activeTab === 'reservations' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('reservations')}
            className="rounded-full"
            data-testid="tab-reservations"
          >
            <User className="w-4 h-4 mr-1" /> Réservations ({reservations.length})
            {pendingReservations.length > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1.5">{pendingReservations.length}</Badge>
            )}
          </Button>
        </div>

        {/* Boxes Tab */}
        {activeTab === 'boxes' && (
          <>
            {boxes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Leaf className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
                  <p className="text-muted-foreground mb-2">Aucun colis anti-gaspi pour le moment</p>
                  <p className="text-xs text-muted-foreground mb-4">Proposez des colis pour réduire le gaspillage et obtenir votre licence offerte !</p>
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Créer un colis
                  </Button>
                </CardContent>
              </Card>
            ) : (
              boxes.map(box => {
                const boxReservations = reservations.filter(r => r.box_id === box.id);
                return (
                  <Card key={box.id} className="shadow-sm" data-testid={`anti-waste-box-${box.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{box.name}</p>
                          {box.description && <p className="text-sm text-muted-foreground mt-1">{box.description}</p>}
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-sm line-through text-muted-foreground">{box.original_value?.toFixed(2)}€</span>
                            <span className="text-lg font-bold text-emerald-600">{box.price?.toFixed(2)}€</span>
                            {box.original_value > 0 && (
                              <Badge className="bg-red-500 text-white">-{Math.round((1 - box.price / box.original_value) * 100)}%</Badge>
                            )}
                          </div>
                          {box.contents && box.contents.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {box.contents.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            Retrait : {box.pickup_start} - {box.pickup_end} | Stock : {box.quantity_available - (box.quantity_sold || 0)} restant(s)
                          </p>
                          {boxReservations.length > 0 && (
                            <p className="text-xs text-emerald-600 mt-1 font-medium">
                              {boxReservations.length} réservation(s)
                            </p>
                          )}
                        </div>
                        <Badge className={box.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100"}>
                          {box.is_active ? "Actif" : "Terminé"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </>
        )}

        {/* Reservations Tab */}
        {activeTab === 'reservations' && (
          <>
            {reservations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-muted-foreground">Aucune réservation pour le moment</p>
                  <p className="text-xs text-muted-foreground mt-1">Les réservations de vos colis apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              reservations.map(r => (
                <Card key={r.id} className={`shadow-sm ${r.status === 'reserved' ? 'border-amber-200 bg-amber-50/30' : ''}`} data-testid={`reservation-${r.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-charcoal" />
                          <span className="font-semibold text-sm">{r.user_pseudo}</span>
                          <Badge className={
                            r.status === 'reserved' ? 'bg-amber-100 text-amber-700' :
                            r.status === 'picked_up' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {r.status === 'reserved' ? 'En attente' :
                             r.status === 'picked_up' ? 'Récupéré' :
                             'Non récupéré'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{r.box_name} — {r.box_price?.toFixed(2)}€</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Retrait : {r.pickup_start} - {r.pickup_end} | Réservé le {new Date(r.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      {r.status === 'reserved' && (
                        <div className="flex gap-2 ml-3">
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 px-3 text-xs"
                            onClick={() => updateReservationStatus(r.id, 'picked_up')}
                            disabled={updatingReservation === r.id}
                            data-testid={`confirm-pickup-${r.id}`}
                          >
                            {updatingReservation === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                            Récupéré
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 text-xs"
                            onClick={() => updateReservationStatus(r.id, 'not_picked_up')}
                            disabled={updatingReservation === r.id}
                            data-testid={`mark-not-picked-${r.id}`}
                          >
                            {updatingReservation === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                            Absent
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </>
        )}
      </div>

      {/* Create form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="anti-waste-form-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau colis anti-gaspi</DialogTitle>
            <DialogDescription>Créez un colis pour réduire le gaspillage alimentaire</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nom du colis</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} data-testid="aw-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Panier de fruits et légumes du jour" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valeur réelle (€) *</Label>
                <Input type="number" min="0" step="0.5" value={form.original_value} onChange={(e) => setForm(p => ({ ...p, original_value: e.target.value }))} placeholder="25.00" data-testid="aw-value" />
              </div>
              <div>
                <Label>Prix de vente (€) *</Label>
                <Input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} placeholder="10.00" data-testid="aw-price" />
              </div>
            </div>
            <div>
              <Label>Quantité disponible</Label>
              <Input type="number" min="1" value={form.quantity_available} onChange={(e) => setForm(p => ({ ...p, quantity_available: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Retrait début</Label>
                <Input type="time" value={form.pickup_start} onChange={(e) => setForm(p => ({ ...p, pickup_start: e.target.value }))} />
              </div>
              <div>
                <Label>Retrait fin</Label>
                <Input type="time" value={form.pickup_end} onChange={(e) => setForm(p => ({ ...p, pickup_end: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Contenu (séparé par des virgules)</Label>
              <Input value={form.contents} onChange={(e) => setForm(p => ({ ...p, contents: e.target.value }))} placeholder="Tomates, Courgettes, Pain, Fromage" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button onClick={createBox} disabled={saving} data-testid="save-anti-waste-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
              Créer le colis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
